// Copyright (c) Docugami, Inc. All rights reserved.

import { useAssignDocSetCommand } from "hooks/AssignDocSetCommand";
import { useDeleteFilesCommand } from "hooks/DeleteFilesCommand";
import { useDownloadFilesCommand } from "hooks/DownloadFilesCommand";
import { useRenameDocSetCommand } from "hooks/RenameDocSetCommand";
import { ClusterInfo } from "networking/api";
import { useMemo } from "react";
import { IDocumentCommand } from "../DocumentCommand";
import { useDownloadDgmlCommand } from "hooks/DownloadDgmlCommand";
import { useUploadToDocsetCommand } from "hooks/UploadToDocsetCommand";
import { useMergeDocSetsCommand } from "hooks/MergeDocsetsCommand";
import { useDocSetDescriptionCommand } from "hooks/DocsetDescription";
import { useDocsetModelStatsCommand } from "hooks/DocsetModelStatsCommand";

interface IDocumentCommands {
    commands: IDocumentCommand[];
    overflowCommands?: IDocumentCommand[];
    overlays: JSX.Element[];
}

interface DocCommandProps {
    selectedItems: string[]
    docSet: ClusterInfo
}

export const useDocumentsCommands = ({ selectedItems, docSet }: DocCommandProps): IDocumentCommands => {
    const uploadToDocsetCommand = useUploadToDocsetCommand(docSet);
    const deleteFilesCommand = useDeleteFilesCommand(selectedItems, () => { });
    const downloadFilesCommand = useDownloadFilesCommand(selectedItems, () => { });
    const mergeDocSetCommand = useMergeDocSetsCommand(docSet);
    const reassignCommand = useAssignDocSetCommand("Reassign", selectedItems, () => { }, selectedItems.length === docSet.numberOfDocuments);
    const renameDocSetCommand = useRenameDocSetCommand(docSet.id || '', () => { });
    const docsetDescriptionCommand = useDocSetDescriptionCommand(docSet.id || '', () => { });
    const downloadDgmlCommand = useDownloadDgmlCommand(selectedItems, docSet.label!);
    const docsetModelStatsCommand = useDocsetModelStatsCommand(docSet.id || '', () => { });

    const visibleCommands = useMemo(() => {
        if (selectedItems.length > 0) {
            return [downloadFilesCommand];
        } else if (!!docSet.isDocugamiCluster) {
            return [];
        } else {
            return [uploadToDocsetCommand];
        }
    },
    [docSet.isDocugamiCluster, uploadToDocsetCommand, downloadFilesCommand, selectedItems]);

    const overflowCommands = useMemo(() => {
        if (selectedItems.length > 0) {
            if (!!docSet.isDocugamiCluster) {
                return [downloadDgmlCommand];
            } else {
                return [downloadDgmlCommand, reassignCommand, deleteFilesCommand];
            }
        } else if (!!docSet.isDocugamiCluster) {
            return [];
        } else {
            return [docsetDescriptionCommand, renameDocSetCommand, mergeDocSetCommand, docsetModelStatsCommand];
        }
    },
    [docSet.isDocugamiCluster, docsetDescriptionCommand,renameDocSetCommand, mergeDocSetCommand, reassignCommand, downloadDgmlCommand, deleteFilesCommand, selectedItems, docsetModelStatsCommand]);

    const overlays = useMemo(() => [...visibleCommands, ...overflowCommands].filter(c => c.isActive && !!c.visual).map(x => x.visual) as JSX.Element[], [visibleCommands, overflowCommands]);

    return {
        commands: visibleCommands,
        overlays: overlays,
        overflowCommands: overflowCommands
    }
}
